/**
 * Geocode Service — Resolve address strings to lat/lng via Google Geocoding API.
 *
 * Three-layer cache:
 *   1. In-memory Map (per session)
 *   2. Firestore `geocode_cache/{hash}` (shared across users, permanent)
 *   3. Google Geocoding API (billed, AU-biased)
 *
 * The cache is keyed by a deterministic hash of the normalized address, so
 * "1 Bong Bong St, Bowral" and " 1  Bong Bong St., Bowral " resolve to the
 * same cache entry and only one API call is ever made per unique address.
 *
 * Requires: VITE_GOOGLE_MAPS_API_KEY with the Geocoding API enabled in
 * Google Cloud Console (separate from the Maps JS / Places APIs).
 */

import { db } from '@/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

export interface GeocodeResult {
  lat: number;
  lng: number;
  formattedAddress?: string;
  cachedAt?: string;
}

// In-memory session cache (null means "we tried and failed — don't retry")
const memCache = new Map<string, GeocodeResult | null>();
// In-flight requests so parallel calls for the same address share one fetch
const inflight = new Map<string, Promise<GeocodeResult | null>>();

export function normalizeAddress(addr: string): string {
  return addr
    .trim()
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Stable, short, doc-id-safe hash of the normalized address */
export function hashAddress(normalized: string): string {
  let h = 5381;
  for (let i = 0; i < normalized.length; i++) {
    h = ((h << 5) + h + normalized.charCodeAt(i)) | 0;
  }
  return `a${Math.abs(h).toString(36)}`;
}

/** Add ", Australia" if no AU indicator present, to bias the geocoder */
function biasToAustralia(address: string): string {
  if (/\b(australia|nsw|vic|qld|wa|sa|act|tas|nt)\b/i.test(address)) {
    return address;
  }
  return `${address}, Australia`;
}

/**
 * Resolve an address to lat/lng. Returns null if the address is empty, the
 * API key is missing, the API rejects the request, or no results are returned.
 * Never throws.
 */
export async function geocodeAddress(address: string): Promise<GeocodeResult | null> {
  if (!address || !address.trim()) return null;

  const normalized = normalizeAddress(address);
  if (!normalized) return null;

  if (memCache.has(normalized)) return memCache.get(normalized)!;
  const existing = inflight.get(normalized);
  if (existing) return existing;

  const task = (async (): Promise<GeocodeResult | null> => {
    const id = hashAddress(normalized);
    const cacheRef = doc(db, 'geocode_cache', id);

    // Layer 2: Firestore cache
    try {
      const snap = await getDoc(cacheRef);
      if (snap.exists()) {
        const data = snap.data() as Partial<GeocodeResult>;
        if (typeof data.lat === 'number' && typeof data.lng === 'number') {
          const hit: GeocodeResult = {
            lat: data.lat,
            lng: data.lng,
            formattedAddress: data.formattedAddress,
            cachedAt: data.cachedAt,
          };
          memCache.set(normalized, hit);
          return hit;
        }
      }
    } catch {
      // Silent — fall through to API
    }

    // Layer 3: Google Geocoding API
    if (!API_KEY) {
      memCache.set(normalized, null);
      return null;
    }

    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        biasToAustralia(address)
      )}&region=au&key=${API_KEY}`;
      const res = await fetch(url);
      const json = await res.json();

      if (json.status !== 'OK' || !Array.isArray(json.results) || json.results.length === 0) {
        memCache.set(normalized, null);
        return null;
      }

      const r = json.results[0];
      const loc = r?.geometry?.location;
      if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
        memCache.set(normalized, null);
        return null;
      }

      const result: GeocodeResult = {
        lat: loc.lat,
        lng: loc.lng,
        formattedAddress: r.formatted_address,
        cachedAt: new Date().toISOString(),
      };
      memCache.set(normalized, result);

      // Fire-and-forget: store in shared Firestore cache for next time / other users
      setDoc(cacheRef, {
        ...result,
        normalized,
        originalAddress: address,
      }).catch(() => {});

      return result;
    } catch {
      memCache.set(normalized, null);
      return null;
    }
  })();

  inflight.set(normalized, task);
  try {
    return await task;
  } finally {
    inflight.delete(normalized);
  }
}
